import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo } from '@/mocks/about';
import { faqList } from '@/mocks/faq';

const values = [
  { icon: 'ri-focus-2-line', label: '使命', text: '为用户提供安全、高效、全面的网络服务平台，让每一个用户都能在 91n.com 找到所需' },
  { icon: 'ri-eye-line', label: '愿景', text: '成为最受用户信赖的网络综合服务平台，持续引领行业服务标准' },
  { icon: 'ri-heart-line', label: '理念', text: '用户至上，服务为本。以用户需求为导向，持续优化平台体验和服务质量' },
];

export default function AboutPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const faqSchema = {
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于 91n.com - 品牌介绍·信誉保障·联系方式"
        description="了解 91n.com 品牌背景、发展历程、核心价值及信誉保障。91n.com 是值得信赖的网络综合服务平台，累计服务超过百万用户。获取官方联系方式。"
        keywords="91n.com关于,91n.com品牌,91n.com联系方式,91n.com信誉,91n.com介绍"
        canonicalUrl="/about"
      />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebPage" data={{
        name: '关于 91n.com - 品牌介绍与联系方式',
        description: '了解 91n.com 品牌背景、发展历程、核心价值及信誉保障',
      }} />
      <SchemaOrg type="FAQPage" data={faqSchema} />
      <SchemaOrg type="BreadcrumbList" />

      <Navbar />

      <main className="pt-16">
        {/* Hero Banner */}
        <section className="relative w-full h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20office%20team%20working%20together%20with%20warm%20atmosphere%2C%20professional%20digital%20company%20culture%2C%20soft%20cream%20and%20beige%20tones%2C%20collaborative%20workspace%2C%20friendly%20environment&width=1600&height=600&seq=about-hero-91n&orientation=landscape"
            alt="关于 91n.com"
            title="91n.com 品牌介绍"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-3xl md:text-4xl font-bold text-background-50 font-heading">
              关于 91n.com
            </h1>
            <p className="mt-3 text-background-50/80 text-sm md:text-base max-w-lg mx-auto">
              了解我们的品牌故事、核心价值与发展历程
            </p>
          </div>
        </section>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb />
        </div>

        {/* Brand Introduction */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                品牌介绍
              </h2>
              <p className="mt-6 text-sm md:text-base text-foreground-600 leading-relaxed">
                {aboutInfo.introduction}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              {values.map((v) => (
                <div key={v.label} className="bg-background-100 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className={`${v.icon} text-2xl`}></i>
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950">{v.label}</h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* History Timeline */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                发展历程
              </h2>
              <p className="mt-3 text-foreground-500 text-sm">
                自成立以来，91n.com 持续成长与创新的每一步
              </p>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="relative pl-8 border-l-2 border-primary-200 space-y-8">
                {aboutInfo.history.map((item) => (
                  <div key={item.year} className="relative">
                    <div className="absolute -left-[2.15rem] top-0.5 w-4 h-4 rounded-full bg-primary-500 border-2 border-background-50"></div>
                    <span className="text-sm font-bold text-primary-500 whitespace-nowrap">{item.year}</span>
                    <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{item.event}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                信誉保障
              </h2>
              <p className="mt-3 text-foreground-500 text-sm">
                百万用户的共同选择，值得信赖的网络服务平台
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutInfo.reputation.map((item) => (
                <div key={item.label} className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950 whitespace-nowrap">{item.label}</h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                联系我们
              </h2>
              <p className="mt-3 text-foreground-500 text-sm">
                如有任何问题或建议，欢迎通过以下方式与我们取得联系
              </p>
            </div>
            <div className="max-w-lg mx-auto bg-background-50 border border-background-200/70 rounded-lg p-8">
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">电子邮箱</p>
                    <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">服务时间</p>
                    <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.serviceTime}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                    <i className="ri-speed-up-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">响应时效</p>
                    <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.responseTime}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ on About Page */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                常见问题
              </h2>
              <p className="mt-3 text-foreground-500 text-sm">
                关于 91n.com，您可能想了解的问题
              </p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqList.slice(0, 6).map((item, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  >
                    <span className="text-sm font-medium text-foreground-950 pr-4">{item.question}</span>
                    <i
                      className={`text-foreground-400 transition-transform duration-300 shrink-0 ${
                        openFaqIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}